/**
 * WeekGrid — 나의 할일 '이번 주' 격자(행 = 현장, 열 = 날짜). 할일 코드는 보이지 않는다.
 * 칸 안 카드 = 공종(굵게) + 할일 짧은 제목 또는 일정 종류(입고 · 착공 · 공사완료).
 * 할일 카드의 점은 SignalDot(신호등) — 자세히 보기 주소(rowHref)가 있으면 눌러서 이동한다(없으면 누를 수 없음).
 * 일정 카드(입고 · 착공 · 공사완료)는 신호등이 아니므로 토큰 배경의 단순 점을 쓰고(D-07 §1.2), 이동 주소가 없어 누를 수 없다.
 * 열은 서버 7일 그대로이되, 비어 있는 일요일은 뺀다(간트 기본 휴무 — 범례에 안내). 클릭 카드는 role="button"·tabIndex·Enter/Space(D-09 §15.28).
 */
import type { KeyboardEvent } from 'react';
import { Card } from '@indipia/ui-core';
import { SignalDot } from './SignalDot';
import type { TodoItem, WeekDayView, WeekEntry } from './todoModel';

/** 현장 칸 1 + 날짜 칸 n — 날짜 칸은 같은 폭. 카드보다 좁아지면 가로 스크롤(최소 폭 = 현장 150 + 날짜 110 × n). */
const SITE_MIN = 150;
const DAY_MIN = 110;
const colTemplate = (days: number) => ({
  gridTemplateColumns: `minmax(${SITE_MIN}px, 1.2fr) repeat(${days}, minmax(${DAY_MIN}px, 1fr))`,
  minWidth: SITE_MIN + DAY_MIN * days,
});

const isSunday = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
};

function EventDot({ type }: { type: string }) {
  // 일정 — 입고 = 진한 점, 착공 · 공사완료 = 연한 점
  return <span aria-hidden className={`inline-block h-2 w-2 shrink-0 rounded-full ${type === 'DELIVERY' ? 'bg-text' : 'bg-text-disabled'}`} />;
}

function EntryCard({ entry, onOpen }: { entry: WeekEntry; onOpen?: (todo: TodoItem) => void }) {
  const todo = entry.kind === 'todo' ? entry.todo : null;
  const linked = todo?.rowHref != null && !!onOpen;
  const open = () => { if (todo && linked) onOpen?.(todo); };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };
  const task = todo ? todo.task : entry.kind === 'event' ? entry.task : '';
  const sub = todo ? (todo.abbr ?? todo.title) : entry.kind === 'event' ? entry.label : '';
  const subTone = todo ? (todo.level === 'r' ? 'text-danger-600' : 'text-warning-600') : 'text-text-muted';
  return (
    <div
      {...(linked ? { role: 'button', tabIndex: 0, onClick: open, onKeyDown } : {})}
      title={[task, sub, todo?.destLabel].filter(Boolean).join(' · ')}
      className={`flex flex-col gap-0.5 rounded-md bg-group-subtle px-2.5 py-2 ${
        linked ? 'cursor-pointer transition-colors hover:bg-point-subtle' : ''
      } ${entry.kind === 'event' && entry.type !== 'DELIVERY' ? 'opacity-70' : ''}`}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {todo ? <SignalDot color={todo.level === 'r' ? 'red' : 'yellow'} size={8} /> : entry.kind === 'event' && <EventDot type={entry.type} />}
        <span className="truncate text-body-sm font-bold text-text">{task || sub}</span>
      </span>
      {task && sub && <span className={`truncate text-caption ${subTone}`}>{sub}</span>}
    </div>
  );
}

interface SiteRow {
  siteId: number;
  site: string;
  owner: string;
  count: number;
  /** 날짜(YYYY-MM-DD) → 그날 카드 */
  byDate: Map<string, WeekEntry[]>;
}

/** 현장 행 — 현장 순서 = 그 현장이 처음 나온 날짜 순(서버 순서). 담당은 그 현장 할일의 담당 표시명. */
function toSiteRows(days: WeekDayView[]): SiteRow[] {
  const rows = new Map<number, SiteRow>();
  for (const d of days) {
    for (const e of d.items) {
      let r = rows.get(e.siteId);
      if (!r) {
        r = { siteId: e.siteId, site: e.site, owner: '', count: 0, byDate: new Map() };
        rows.set(e.siteId, r);
      }
      if (!r.owner && e.kind === 'todo' && e.todo.owner) r.owner = e.todo.owner;
      r.count += 1;
      const list = r.byDate.get(d.date) ?? [];
      list.push(e);
      r.byDate.set(d.date, list);
    }
  }
  return [...rows.values()];
}

export function WeekGrid({
  allDays, days, showOwner, onOpen,
}: {
  /** 서버 7일 전체(열 머리) */
  allDays: WeekDayView[];
  /** 필터 적용 후 줄이 있는 날짜(칸 내용) */
  days: WeekDayView[];
  /** 담당자 표시 — 담당자 필터가 '전체'일 때 */
  showOwner: boolean;
  onOpen?: (todo: TodoItem) => void;
}) {
  const countByDate = new Map(days.map((d) => [d.date, d.items.length] as const));
  const columns = allDays.filter((d) => !isSunday(d.date) || (countByDate.get(d.date) ?? 0) > 0);
  const sundayHidden = columns.length < allDays.length;
  const rows = toSiteRows(days);
  const style = colTemplate(columns.length);
  const todayCol = (d: WeekDayView) => (d.today ? 'bg-point-subtle' : '');

  return (
    <Card className="[&>.body]:p-0">
      <div className="overflow-x-auto">
        <div className="grid w-full" style={style}>
          {/* 머리 행 — 현장 수 · 날짜(오늘 강조) · 건수 */}
          <div className="border-b border-r border-border px-4 py-3 text-body-sm font-bold text-text-secondary">
            현장 <span className="text-caption font-normal text-text-muted">{rows.length}곳</span>
          </div>
          {columns.map((d, i) => (
            <div
              key={d.date}
              className={`border-b border-border px-3 py-3 text-body-sm font-bold ${i < columns.length - 1 ? 'border-r' : ''} ${
                d.today ? 'bg-point-subtle text-point' : 'text-text'
              }`}
            >
              {d.label}{d.today && ' 오늘'}{' '}
              <span className="text-caption font-normal text-text-muted">{countByDate.get(d.date) ?? 0}건</span>
            </div>
          ))}

          {/* 현장 행 */}
          {rows.map((r, ri) => {
            const last = ri === rows.length - 1;
            return [
              <div key={`${r.siteId}:site`} className={`flex min-w-0 flex-col justify-center gap-0.5 border-r border-border px-4 py-3 ${last ? '' : 'border-b'}`}>
                <span className="truncate text-body-md font-bold text-text" title={r.site}>{r.site}</span>
                <span className="truncate text-caption text-text-muted">
                  {showOwner && r.owner ? `담당 ${r.owner} · ` : ''}할일 {r.count}건
                </span>
              </div>,
              ...columns.map((d, ci) => (
                <div
                  key={`${r.siteId}:${d.date}`}
                  className={`flex min-w-0 flex-col gap-1.5 border-border p-2 ${last ? '' : 'border-b'} ${ci < columns.length - 1 ? 'border-r' : ''} ${todayCol(d)}`}
                >
                  {(r.byDate.get(d.date) ?? []).map((e) => <EntryCard key={e.id} entry={e} onOpen={onOpen} />)}
                </div>
              )),
            ];
          })}
        </div>
      </div>

      {/* 범례 */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border px-4 py-3 text-caption text-text-muted">
        <span className="inline-flex items-center gap-1.5"><SignalDot color="red" size={8} />지금 처리</span>
        <span className="inline-flex items-center gap-1.5"><SignalDot color="yellow" size={8} />오늘 일정</span>
        <span className="inline-flex items-center gap-1.5"><EventDot type="DELIVERY" />입고</span>
        <span className="inline-flex items-center gap-1.5"><EventDot type="START" />착공 · 공사완료</span>
        <span>· 할일 칸을 누르면 해당 화면으로 · 마우스를 올리면 상세</span>
        {sundayHidden && <span>· 일요일은 간트 기본 휴무라 비어 있으면 빼고 보여요</span>}
      </div>
    </Card>
  );
}
